import { Hand, Mic, MicOff, MoveDown, PhoneOff, Radio, UserRoundCheck, Users, X } from 'lucide-react';
import { isStageModerator, Permission, type VoiceState } from '@spawnwire/shared';
import { Avatar, Button, clsx, ContextMenu, DropdownMenu, IconButton, Spinner, type MenuEntry } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { can, useChannelPermissions } from '../../lib/perms';
import { joinVoice, leaveVoice, stageUpdate, useVoice } from '../../lib/voice';
import { displayNameIn, useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { userMenu } from '../members/userMenu';
import { CallControls } from './VoiceView';

const run = (p: Promise<void>) => void p.catch((err) => ui.toast(errorMessage(err), 'error'));

function stageEntries(state: VoiceState, channelId: string, moderator: boolean, meId: string | undefined): MenuEntry[] {
  if (!moderator) return [];
  const self = state.userId === meId;
  return [
    { label: 'Invite to speak', icon: Mic, hidden: !state.suppress, onSelect: () => run(stageUpdate({ userId: self ? undefined : state.userId, suppress: false }, channelId)) },
    { label: 'Move to audience', icon: MoveDown, hidden: state.suppress, onSelect: () => run(stageUpdate({ userId: self ? undefined : state.userId, suppress: true }, channelId)) },
  ];
}

function Person({ state, channelId, spaceId, moderator, big }: { state: VoiceState; channelId: string; spaceId: string; moderator: boolean; big?: boolean }) {
  const user = useStore((s) => s.users[state.userId]);
  const name = useStore((s) => displayNameIn(s, state.userId, spaceId));
  const meId = useStore((s) => s.me?.id);
  const speaking = useVoice((v) => !!v.speaking[state.userId]);
  const muted = state.selfMute || state.serverMute;
  const entries = [...stageEntries(state, channelId, moderator, meId), ...userMenu(state.userId, spaceId, { voiceChannelId: channelId })];
  return (
    <ContextMenu entries={entries}>
      <li className={clsx('group relative flex flex-col items-center gap-1.5 rounded-xl p-2 text-center', big ? 'w-32' : 'w-20')}>
        <span className="relative">
          <Avatar user={user} size={big ? 80 : 48} speaking={big && speaking && !muted} />
          {big && muted && (
            <span className="absolute -bottom-1 -right-1 rounded-full bg-main p-1 text-danger">
              <MicOff className="size-3.5" aria-label="Muted" />
            </span>
          )}
          {!big && state.requestToSpeakAt && (
            <span className="absolute -right-1 -top-1 rounded-full bg-warning p-1 text-black">
              <Hand className="size-3" aria-label="Wants to speak" />
            </span>
          )}
        </span>
        <span className={clsx('w-full truncate', big ? 'text-sm font-medium text-fg' : 'text-xs text-muted')}>
          {name}
          {state.userId === meId ? ' (you)' : ''}
        </span>
        {moderator && entries.length > 0 && (
          <DropdownMenu entries={entries} trigger={<button type="button" className="absolute inset-0 rounded-xl opacity-0" aria-label={`Actions for ${name}`} />} />
        )}
      </li>
    </ContextMenu>
  );
}

/** Stage channels: speakers on stage, an audience that listens, and hands raised to speak. */
export function StageView({ channelId }: { channelId: string }) {
  const channel = useStore((s) => s.channels[channelId]);
  const states = useStore((s) => s.voiceStates[channelId]) ?? {};
  const meId = useStore((s) => s.me?.id);
  const perms = useChannelPermissions(channelId);
  const connectedHere = useVoice((v) => v.channelId === channelId);
  const status = useVoice((v) => v.status);
  if (!channel?.spaceId) return null;
  const spaceId = channel.spaceId;
  const moderator = isStageModerator(perms);
  const all = Object.values(states).sort((a, b) => a.joinedAt.localeCompare(b.joinedAt));
  const speakers = all.filter((s) => !s.suppress);
  const audience = all.filter((s) => s.suppress);
  const requests = audience.filter((s) => s.requestToSpeakAt).sort((a, b) => a.requestToSpeakAt!.localeCompare(b.requestToSpeakAt!));
  const mine = meId ? states[meId] : undefined;
  const canConnect = can(perms, Permission.CONNECT);
  const canRequest = can(perms, Permission.REQUEST_TO_SPEAK);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-rail">
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto max-w-4xl space-y-6">
          <div className="rounded-xl border border-line bg-sidebar p-4">
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-success">
              <Radio className="size-4" aria-hidden /> {speakers.length > 0 ? 'Live on stage' : 'Stage'}
            </p>
            <h2 className="mt-1 text-xl font-bold text-fg">{channel.topic || channel.name}</h2>
            <p className="mt-1 flex items-center gap-3 text-sm text-muted">
              <span>
                {speakers.length} speaker{speakers.length === 1 ? '' : 's'}
              </span>
              <span className="flex items-center gap-1">
                <Users className="size-4" aria-hidden /> {audience.length} listening
              </span>
            </p>
          </div>

          {moderator && requests.length > 0 && (
            <section aria-label="Requests to speak" className="rounded-xl border border-warning/50 bg-sidebar p-4">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-fg">
                <Hand className="size-4 text-warning" aria-hidden /> Requests to speak
              </h3>
              <ul className="space-y-1">
                {requests.map((r) => (
                  <li key={r.userId} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-hover">
                    <Avatar user={useStore.getState().users[r.userId]} size={28} />
                    <span className="min-w-0 flex-1 truncate text-sm text-fg">{displayNameIn(useStore.getState(), r.userId, spaceId)}</span>
                    <Button size="sm" icon={UserRoundCheck} onClick={() => run(stageUpdate({ userId: r.userId, suppress: false }, channelId))}>
                      Invite to speak
                    </Button>
                    <IconButton icon={X} label="Dismiss request" onClick={() => run(stageUpdate({ userId: r.userId, suppress: true }, channelId))} />
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section aria-label="Speakers">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Speakers</h3>
            {speakers.length === 0 ? (
              <p className="rounded-xl border border-dashed border-line p-6 text-center text-sm text-muted">
                No one is speaking yet.{moderator ? ' Join and choose “Become a speaker” to start.' : ''}
              </p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {speakers.map((s) => (
                  <Person key={s.userId} state={s} channelId={channelId} spaceId={spaceId} moderator={moderator} big />
                ))}
              </ul>
            )}
          </section>

          {audience.length > 0 && (
            <section aria-label="Audience">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">Audience · {audience.length}</h3>
              <ul className="flex flex-wrap gap-1">
                {audience.map((s) => (
                  <Person key={s.userId} state={s} channelId={channelId} spaceId={spaceId} moderator={moderator} />
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>

      <div className="shrink-0 border-t border-line bg-sidebar p-4">
        {!connectedHere ? (
          <div className="flex justify-center">
            <Button size="lg" icon={Radio} disabled={!canConnect} onClick={() => void joinVoice(channelId)}>
              {canConnect ? 'Join stage' : 'You cannot join this stage'}
            </Button>
          </div>
        ) : status === 'connecting' || !mine ? (
          <div className="flex justify-center gap-2 text-sm text-muted">
            <Spinner className="size-4" /> Connecting
          </div>
        ) : mine.suppress ? (
          <div className="flex flex-wrap items-center justify-center gap-3">
            <span className="text-sm text-muted">You are in the audience.</span>
            {moderator ? (
              <Button icon={Mic} onClick={() => run(stageUpdate({ suppress: false }))}>
                Become a speaker
              </Button>
            ) : (
              canRequest && (
                <Button icon={Hand} variant={mine.requestToSpeakAt ? 'secondary' : 'primary'} onClick={() => run(stageUpdate({ requestToSpeak: !mine.requestToSpeakAt }))}>
                  {mine.requestToSpeakAt ? 'Lower hand' : 'Request to speak'}
                </Button>
              )
            )}
            <Button icon={PhoneOff} variant="danger" onClick={() => void leaveVoice()}>
              Leave
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-4">
            <CallControls />
            <Button variant="secondary" icon={MoveDown} onClick={() => run(stageUpdate({ suppress: true }))}>
              Move to audience
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
